import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
} from '@denishussar/html5-game-engine';
import {BoosterTypeEnum, GameModel} from "../../Models/GameModel";
import {AnimationContents} from "../../../AnimationContents";
import {ButtonController} from "../../Common/ButtonController";

import {UIDailyRewardController, UIDailyRewardProps, UIDailyRewardType} from "../Screens/Main/UIDailyRewardController";
import {
    GreenButtonTextStyle,
    YellowTextBoldStyle,
    YellowTextStyle
} from "../../Constants/TextStyles";
import {LocalizationContents} from "../../LocalizationContents";
import {GameEvent, GameEventPopupButtonName, GameEventPopupName} from "../../Constants/GameEvent";

export interface IDailyRewardModalControllerOptions {
    day: number;
    rewards: UIDailyRewardProps[];
    claimText: string;
    closeClick: () => void;
    claimClick: (reward: UIDailyRewardProps) => void;
}

export class DailyRewardModalController extends AnimationBaseController {
    $model: GameModel;

    private timerId;

    public constructor(
        @Inject private _options: IDailyRewardModalControllerOptions,
        @Inject private modelEvents: ModelEvents,
        @Inject private model: GameModel,
    ) {
        super('DailyRewardModalController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.DAILY_REWARD_SCREEN,
            libraryName: 'daily_reward_modal',
            childControllers: {
                btn_close: {
                    controller: ButtonController,
                    instanceName: 'content.btn_close',
                    options: {
                        onClick: () => {
                            if (this._options.closeClick) {
                                this._options.closeClick();
                            }
                        },
                    },
                },
                btn_claim: {
                    controller: ButtonController,
                    instanceName: 'content.btn_claim',
                    options: {
                        label: this._options.claimText,
                        onClick: (status) => {
                            this.modelEvents.dispatch(this.$model, GameEvent.popupButtonClick, { popupName: GameEventPopupName.dailyPrize, buttonName: GameEventPopupButtonName.claim, status });

                            if (!status) {
                                return;
                            }

                            if (this._options.claimClick) {
                                this._options.claimClick(this._options.rewards[this._options.day]);
                            }
                        },
                    },
                },
            },
        };
    }

    protected onInitialize() {
        this.setTextByQuery('content.btn_claim.label', LocalizationContents.getText('Receive!'));
        this.setTextStyleByQuery('content.btn_claim.label', GreenButtonTextStyle);
    }

    protected onDestroy() {
        clearTimeout(this.timerId);

        super.onDestroy();
    }

    protected onResize(width: number, height: number, scale: number) {
    }

    public init(): void {
        this.setTextStyleByQuery(`content.txt_title`, YellowTextStyle);

        for (let i = 0; i < 7; i++) {
            this.setupItem(i);
        }

        this.timerUpdate();
    }

    public release(): void {
        clearTimeout(this.timerId);
    }

    private isRewardAvailable(): boolean {
        return this.model.addsDay === 0 ? true : Date.now() - this.$model.addsCooldownTimestamp >= UIDailyRewardController.REWARD_COOLDOWN;
    }

    private timerUpdate(): void {
        const btnClaim = this.$childControllers.get('btn_claim') as ButtonController;
        if (this.isRewardAvailable()) {
            btnClaim.enabled();
        } else {
            btnClaim.disabled();
        }

        this.timerId = setTimeout(() => this.timerUpdate(), 1000);
    }

    private setupItem(day: number): void {
        const activeDay = this._options.day;
        const active = day === activeDay;
        const rewardConfig = this._options.rewards[day];

        const key = `content.item${day}`;

        this.gotoAndStopByQuery(key, active ? 'active' : 'inactive');
        this.setTextByQuery(`${key}.txt_title`, active ? LocalizationContents.getText('Today') : `${LocalizationContents.getText('Day')} ${day + 1}`);
        this.getDisplayObjectByQuery(`${key}.check`)[0].visible = day < activeDay;

        const { type, count, size, coins } = rewardConfig;

        switch (type) {
            case UIDailyRewardType.COINS:
                this.setTextByQuery(`${key}.txt_desc`, `x${count}`);
                this.gotoAndStopByQuery(`${key}.prize`, 'coin');
                break;

            case UIDailyRewardType.BOOSTER_NEEDLE:
                this.setTextByQuery(`${key}.txt_desc`, `x${count}`);
                this.gotoAndStopByQuery(`${key}.prize`, 'booster_1');
                break;

            case UIDailyRewardType.BOOSTER_WOOL:
                this.setTextByQuery(`${key}.txt_desc`, `x${count}`);
                this.gotoAndStopByQuery(`${key}.prize`, 'booster_2');
                break;

            case UIDailyRewardType.BOOSTER_COMBO:
                const countA = Array.isArray(count) ? count[0] : count;
                const countB = Array.isArray(count) ? count[1] : count;

                this.setTextByQuery(`${key}.txt_desc`, `x${countA} x${countB}`);
                this.gotoAndStopByQuery(`${key}.prize`, 'booster_combo');
                break;

            case UIDailyRewardType.PICTURE:
                this.setTextByQuery(`${key}.txt_desc`, `${size}x${size}`);
                this.gotoAndStopByQuery(`${key}.prize`,  'picture');
                break;
        }

        this.setTextStyleByQuery(`${key}.txt_desc`, YellowTextBoldStyle);
        this.getDisplayObjectByQuery(`${key}.prize`)[0].scale.set(day === 6 ? 2 : 1);
    }
}
